import { Grid3x3, List } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  type ClinicId,
  type ScheduleOverrideWithRelations,
  toWallClockDate,
} from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/frontend/components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/frontend/components/ui/toggle-group'
import { useRegionClinics } from '@/frontend/features/region/use-region-clinics'
import { ClinicCoverageGrid } from '@/frontend/features/schedules/clinic-coverage-grid'
import { DeleteShiftDialog } from '@/frontend/features/schedules/delete-shift-dialog'
import type { ScheduleOverrideFormValues } from '@/frontend/features/schedules/schedule-override.form'
import {
  ScheduleOverrideFormDialog,
  type ScheduleOverrideFormState,
} from '@/frontend/features/schedules/schedule-override-form-dialog'
import { ScheduleOverridesTable } from '@/frontend/features/schedules/schedule-overrides-table'
import { ScheduleTemplateEditor } from '@/frontend/features/schedules/schedule-template-editor'
import { StaffRotationGrid } from '@/frontend/features/schedules/staff-rotation-grid'
import { useListScheduleOverrides } from '@/frontend/features/schedules/use-list-schedule-overrides'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'

/** Empty default values for the Add override dialog, hoisted to avoid recreating each render. */
const EMPTY_OVERRIDE: ScheduleOverrideFormValues = {
  staffId: '',
  date: '',
  clinicId: null,
  templateId: '',
  note: '',
}

/** Today + 90 days as the list view's window (clinic wall-clock YYYY-MM-DD). */
function defaultRange(): { from: string; to: string } {
  return {
    from: toWallClockDate(new Date(), 'Europe/London'),
    to: toWallClockDate(new Date(Date.now() + 90 * 86_400_000), 'Europe/London'),
  }
}

/** Page for viewing clinic vet coverage and managing per-day shifts across all clinics. */
export function SchedulePage() {
  useDocumentTitle('Booking Schedules')
  // Coverage owns the shifts (grid/list); the other two tabs are self-contained editors.
  const [tab, setTab] = useState<'coverage' | 'rotation' | 'templates'>('coverage')
  const [view, setView] = useState<'coverage' | 'list'>('coverage')
  const [range] = useState(defaultRange)
  // null = all clinics in the region; narrows the coverage grid's rows.
  const [clinicId, setClinicId] = useState<ClinicId | null>(null)
  // '' = All staff; filters the list view.
  const [staffId, setStaffId] = useState('')
  const [formState, setFormState] = useState<ScheduleOverrideFormState>(null)
  const [removing, setRemoving] = useState<ScheduleOverrideWithRelations | null>(null)

  const { data: staff = [] } = useListStaff()
  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, s])), [staff])

  const regionClinics = useRegionClinics({ includeArchived: true })
  const regionClinicIds = useMemo(() => regionClinics.map((c) => c.id), [regionClinics])
  const gridClinics = clinicId ? regionClinics.filter((c) => c.id === clinicId) : regionClinics

  const {
    data: overrides = [],
    isLoading,
    error,
  } = useListScheduleOverrides({
    ...range,
    clinicIds: regionClinicIds,
  })

  const openAdd = (partial: Partial<ScheduleOverrideFormValues> = {}) => {
    setFormState({ mode: 'add', defaults: { ...EMPTY_OVERRIDE, ...partial } })
  }

  const openEdit = (override: ScheduleOverrideWithRelations) =>
    setFormState({ mode: 'edit', override })

  const handleViewChange = (values: string[]) => {
    // Ignore deselect (empty array) — keep the current view
    if (values.length === 0) return
    setView(values[values.length - 1] as 'coverage' | 'list')
  }

  const filteredOverrides = staffId ? overrides.filter((o) => o.staffId === staffId) : overrides

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-headline-lg">Booking Schedules</h1>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        <TabsList>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="rotation">Base rotation</TabsTrigger>
          <TabsTrigger value="templates">Schedule templates</TabsTrigger>
        </TabsList>

        <TabsContent value="coverage">
          <div className="mb-4 flex items-center justify-end gap-3">
            {view === 'coverage' ? (
              <ClinicSearch
                includeArchived
                value={clinicId}
                onChange={(id) => setClinicId(id)}
                allowClear
                placeholder="All clinics"
                className="w-48"
              />
            ) : (
              <StaffSearch
                value={staffId || null}
                onChange={(s) => setStaffId(s?.id ?? '')}
                allowClear
                placeholder="All staff"
                className="w-48"
              />
            )}
            <ToggleGroup
              value={[view]}
              onValueChange={handleViewChange}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="coverage" aria-label="Coverage view">
                <Grid3x3 className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="List view">
                <List className="size-4" />
              </ToggleGroupItem>
            </ToggleGroup>
            <Button size="lg" onClick={() => openAdd({ clinicId: clinicId ?? undefined })}>
              Add one-off shift
            </Button>
          </div>

          {view === 'coverage' ? (
            <ClinicCoverageGrid
              clinics={gridClinics}
              onAdd={({ clinicId, date }) => openAdd({ clinicId, date })}
              onEdit={openEdit}
            />
          ) : error ? (
            <p className="text-destructive-fg">Error: {error.message}</p>
          ) : isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : filteredOverrides.length === 0 ? (
            <p className="text-muted-foreground">No upcoming shifts.</p>
          ) : (
            <ScheduleOverridesTable
              overrides={filteredOverrides}
              staffById={staffById}
              onEdit={openEdit}
              onDelete={setRemoving}
            />
          )}
        </TabsContent>

        <TabsContent value="rotation">
          <StaffRotationGrid />
        </TabsContent>

        <TabsContent value="templates">
          <ScheduleTemplateEditor />
        </TabsContent>
      </Tabs>

      <ScheduleOverrideFormDialog
        state={formState}
        onClose={() => setFormState(null)}
        // Hand off to the confirmation dialog (close the form, open delete confirm).
        onDelete={(override) => {
          setRemoving(override)
          setFormState(null)
        }}
      />

      <DeleteShiftDialog override={removing} onClose={() => setRemoving(null)} />
    </div>
  )
}
